import { Component, HostListener, OnInit, AfterViewInit, signal, inject, ViewChild, ElementRef } from '@angular/core';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { ChallengeProgressiveReward, LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { SharedModule } from '@app/shared/shared.module';
import { EventManager } from '@app/shared/utils/event-manager';
import {
  EventProgressBarData,
  EventProgressBarComponent,
} from '@app/shared/components/event-progress-bar/event-progress-bar.component';
import { firstValueFrom, BehaviorSubject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MoveWithArrowsDirective } from '@app/shared/directives/move-with-arrows.directive';
import { NativeService } from '@app/shared/services/native.service';
import { ProgressLineFacade } from '@app/progress-line/progress-line.facade';
import { StreakComponent } from './streak/streak.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ChallengeCardComponent } from './challenge-card/challenge-card.component';
import { AudioService } from '@app/audio/audio.service';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { ProgressLineHeaderComponent } from '@app/layout/slotcom-header/components/progress-line-header/progress-line-header.component';
import {
  FlyingAssetsComponent,
  FlyingAssetsConfig,
} from '@app/shared/animations/flying-assets/flying-assets.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-daily-challenge',
  templateUrl: './daily-challenge.component.html',
  styleUrls: ['./daily-challenge.component.scss'],
  standalone: true,
  imports: [
    WalletComponent,
    AnimatedTimerComponent,
    EventProgressBarComponent,
    ChallengeCardComponent,
    MoveWithArrowsDirective,
    StreakComponent,
    ProgressLineHeaderComponent,
    SharedModule,
    FlyingAssetsComponent,
  ],
  animations: [
    trigger('unlockAnimation', [
      state('locked', style({ transform: 'scale(1)' })),
      state('unlocked', style({ transform: 'scale(1)' })),
      transition('locked => unlocked', [
        animate('100ms ease-out', style({ transform: 'scale(0.7)' })),
        animate('100ms ease-out', style({ transform: 'scale(1.2)' })),
        animate('100ms ease-out', style({ transform: 'scale(1)' })),
      ]),
    ]),
    trigger('redeemAnimation', [
      state('unlocked', style({ transform: 'scale(1)' })),
      state('redeemed', style({ transform: 'scale(1)' })),
      transition('unlocked => redeemed', [
        animate('150ms ease-out', style({ transform: 'scale(0.2)', opacity: '0' })),
        animate('150ms ease-out', style({ transform: 'scale(1)', opacity: '1' })),
      ]),
      transition('void => redeemed', [
        style({ transform: 'scale(0.2)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class DailyChallengeModalComponent implements OnInit, AfterViewInit {
  @HostListener('window:resize') onResizeWindow() {
    this.checkArrows();
    this.manageSliderArrowVisibility();
  }

  @ViewChild('flyingAssets') flyingAssets!: FlyingAssetsComponent;
  @ViewChild(ProgressLineHeaderComponent, { read: ElementRef })
  progressLineHeaderRef!: ElementRef<HTMLElement>;

  readonly prestigeAssetsConfig: FlyingAssetsConfig = {
    themingGroup: 'progressline',
    themingAsset: 'prestige-logo',
    numAssets: 6,
    creationRadius: 100,
    creationDelay: { min: 100, max: 500 },
    speed: { min: 12, max: 15 },
  };

  activeFlyingAssetsConfig: FlyingAssetsConfig = this.prestigeAssetsConfig;

  private animationOriginSubject = new BehaviorSubject<HTMLElement | null>(null);
  animationOrigin$ = this.animationOriginSubject.asObservable();

  eventProgressBarData: EventProgressBarData[];
  dailyChallenge: LimitedTimeEvent;
  completedChallenges = signal(0);
  redeemedChallenges = signal(0);
  blockedChallengesList: number[] = [];

  showArrows: boolean;
  showScrollNext = signal(true);
  showScrollBack = signal(false);

  showDailyChallenge = signal(false);
  newChallengesComing = signal(false);

  private componentDestroy: () => Observable<unknown>;

  get dailyChallengeScrollElementSelector() {
    return document.querySelector('.c-dailyChallenge__container');
  }

  private readonly logger = inject(LoggerService);
  private readonly eventManager = inject(EventManager);
  private readonly audioService = inject(AudioService);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly nativeService = inject(NativeService);
  private readonly progressLineFacade = inject(ProgressLineFacade);

  ngOnInit() {
    this.eventsRepo.dailyChallenge$.pipe(takeUntil(this.componentDestroy())).subscribe((event) => {
      this.dailyChallenge = event;

      this.manageDailyChallengeVisibility();

      if (this.dailyChallenge && this.dailyChallenge.properties.challengestatuses.length) {
        this.evaluateChallengesStatus();
        this.manageSliderArrowVisibility();
        this.newChallengesComing.set(false);

        this.eventProgressBarData = this.dailyChallenge
          ? this.mapChallengeProgressiveToEventProgressBarData(this.dailyChallenge.properties.challengeprogressive)
          : [];
      }
    });
  }

  ngAfterViewInit() {
    this.manageSliderArrowVisibility();
  }

  openInfo(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.InfoDailyChallengeComponent, ModalType.View, this.dailyChallenge)
    );
  }

  onPrestigeRedeemed(originElement: HTMLElement): void {
    const target = this.progressLineHeaderRef?.nativeElement;
    if (!target || !originElement) return;

    this.activeFlyingAssetsConfig = { ...this.prestigeAssetsConfig };
    this.animationOriginSubject.next(originElement);

    requestAnimationFrame(() => {
      this.flyingAssets?.recalculateCoordinates();
      this.flyingAssets?.triggerAnimation(target, this.prestigeAssetsConfig);
    });
  }

  manageSliderArrowVisibility(): void {
    const element = this.getScrollContainerElement();
    const secureMargin = 30;

    if (element.scrollLeft + element.clientWidth >= element.scrollWidth - secureMargin) {
      this.showScrollNext.set(false);
    } else {
      this.showScrollNext.set(true);
    }

    if (element.scrollLeft < 2) {
      this.showScrollBack.set(false);
    } else {
      this.showScrollBack.set(true);
    }
  }

  async collectProgressiveReward(positionPoints: number): Promise<void> {
    this.audioService.playButtonAudio('reward');

    try {
      const event = await firstValueFrom(
        this.limitedTimeEventService.redeemGameProgressiveChallenge(this.dailyChallenge.eventId, positionPoints)
      );

      if (event) {
        this.limitedTimeEventService.updateLimitedTimeEventById(event);
      }
    } catch (err) {
      this.logger.error(err);
    }
  }

  manageDailyChallengeVisibility(): void {
    if (
      this.dailyChallenge &&
      new Date(this.dailyChallenge.endDate).getTime() > new Date().getTime() &&
      this.dailyChallenge.properties.challengestatuses
    ) {
      this.showDailyChallenge.set(true);
    } else {
      this.showDailyChallenge.set(false);
    }
  }

  challengesEnded(): void {
    this.newChallengesComing.set(true);
    this.limitedTimeEventService.updateDailyChallenges();
  }

  dailyChallengeExpired(): void {
    this.closeModal();
    if (this.dailyChallenge) {
      this.eventsRepo.removeLimitedTimeEventById(this.dailyChallenge.eventId);
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.DailyChallengeModalComponent, ModalType.View));
  }

  private evaluateChallengesStatus(): void {
    this.evaluateBlockedChallenges();
    this.completedChallenges.set(this.eventsRepo.getCompletedChallenges(this.dailyChallenge));
    this.redeemedChallenges.set(
      this.dailyChallenge.properties.challengestatuses.filter((status) => status.redeemed).length
    );
  }

  private evaluateBlockedChallenges(): void {
    const nonBlockedChallenges = this.dailyChallenge.properties.challengestatuses.filter((status) => !status.locked);

    const blockedChallengesNumber = this.dailyChallenge.properties.challengestatuses.filter(
      (status) => status.locked
    ).length;

    this.blockedChallengesList = Array.from(
      { length: blockedChallengesNumber },
      (_, i) => blockedChallengesNumber - i
    ).reverse();

    nonBlockedChallenges.forEach(() => {
      this.blockedChallengesList.unshift(0);
    });
  }

  private getScrollContainerElement(): HTMLElement {
    return document.getElementById('dailyChallenge-scroll-container');
  }

  private mapChallengeProgressiveToEventProgressBarData(
    challengeProgressive: ChallengeProgressiveReward[]
  ): EventProgressBarData[] {
    let eventProgressBarData: EventProgressBarData[] = [];

    eventProgressBarData = challengeProgressive.map((cp) => ({
      points: cp.position,
      reward: cp.reward.credits > 0 ? cp.reward.credits : cp.reward.gems,
      currency: cp.reward.credits > 0 ? 'Coins' : 'Gems',
      redeemed: cp.redeemed,
    }));

    eventProgressBarData.unshift({ points: 0, reward: 0 });

    return eventProgressBarData;
  }

  private checkArrows(): void {
    if (!this.nativeService.isNative()) {
      setTimeout(() => {
        this.showArrows =
          this.dailyChallengeScrollElementSelector &&
          this.dailyChallengeScrollElementSelector.clientWidth !== this.dailyChallengeScrollElementSelector.scrollWidth;
      }, 0);
    }
  }
}
